import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Users, Plus, Search, Edit, Trash2, MapPin, RefreshCw, Loader2, X,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import Sidebar from "../components/dashboard/Sidebar";

const EMPTY = { name: "", email: "", phone: "", company: "", address: "", pin: "", acres: "", notes: "", coordinates: "", location_source: "address", display_address: "" };

const CustomersPage = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // customer id or null (new)
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [propInfo, setPropInfo] = useState(null);
  const [preLookup, setPreLookup] = useState(null); // snapshot for "undo lookup"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/customers`);
      setCustomers(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return customers;
    return customers.filter((c) =>
      [c.name, c.email, c.phone, c.company, c.address, c.pin]
        .some((v) => (v || "").toLowerCase().includes(t)),
    );
  }, [customers, search]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setPropInfo(null); setPreLookup(null); setDialogOpen(true); };
  const openEdit = (c) => {
    setEditing(c.id);
    setForm({
      name: c.name || "", email: c.email || "", phone: c.phone || "", company: c.company || "",
      address: c.address || "", pin: c.pin || "", acres: c.acres != null ? String(c.acres) : "", notes: c.notes || "",
      coordinates: c.coordinates || "", location_source: c.location_source || "address", display_address: c.display_address || c.address || "",
    });
    setPropInfo(c.property_data || null);
    setPreLookup(null);
    setDialogOpen(true);
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Address edits keep the (still-empty) display address in sync so it auto-fills.
  const onAddressChange = (v) => setForm((f) => ({
    ...f,
    address: v,
    display_address: (!f.display_address || f.display_address === f.address) ? v : f.display_address,
  }));

  const runLookup = async () => {
    if (!form.address.trim()) { toast.error("Enter an address first"); return; }
    setLookingUp(true);
    try {
      const res = await axios.get(`${API}/customers/property-lookup`, { params: { address: form.address.trim() } });
      if (res.data?.found) {
        const pd = res.data.property_data;
        // Snapshot what the user had BEFORE the lookup so they can undo a wrong match.
        setPreLookup({ acres: form.acres, pin: form.pin, propInfo });
        setPropInfo(pd);
        setForm((f) => ({
          ...f,
          acres: pd.acreage != null ? String(Math.round(pd.acreage * 100) / 100) : f.acres,
          pin: f.pin || pd.parcel_id || "",
        }));
        toast.success("Property data found");
      } else {
        toast.error("No NC parcel found for that address");
      }
    } catch {
      toast.error("Lookup failed");
    } finally {
      setLookingUp(false);
    }
  };

  const clearLookup = () => {
    // Restore the pre-lookup values (or just clear if there is no snapshot).
    if (preLookup) {
      setForm((f) => ({ ...f, acres: preLookup.acres, pin: preLookup.pin }));
      setPropInfo(preLookup.propInfo || null);
    } else {
      setPropInfo(null);
    }
    setPreLookup(null);
    toast.info("Parcel data cleared");
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      company: form.company.trim() || null,
      address: form.address.trim() || null,
      pin: form.pin.trim() || null,
      acres: form.acres ? Number(form.acres) : null,
      coordinates: form.coordinates.trim() || null,
      location_source: form.location_source || "address",
      display_address: form.display_address.trim() || form.address.trim() || null,
      notes: form.notes.trim() || null,
      // Never auto-pull parcel data on save — it was overwriting good entries with the
      // wrong nearby parcel. Lookups happen only via the explicit "Look up" button, and
      // we persist exactly the parcel blob currently shown (or null if cleared).
      auto_lookup: false,
      property_data: propInfo,
    };
    try {
      if (editing) {
        await axios.put(`${API}/customers/${editing}`, payload);
        toast.success("Customer updated");
      } else {
        await axios.post(`${API}/customers`, payload);
        toast.success("Customer added");
      }
      setDialogOpen(false);
      await load();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/customers/${c.id}`);
      toast.success("Customer deleted");
      setCustomers((prev) => prev.filter((x) => x.id !== c.id));
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar onLogout={() => { logout(); navigate("/login"); }} />
      <main className="flex-1 p-6 md:p-10 overflow-x-hidden">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Users className="w-7 h-7 text-[#00341d]" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
              <p className="text-sm text-gray-500">{customers.length} total</p>
            </div>
          </div>
          <Button onClick={openNew} className="bg-[#00341d] hover:bg-[#00251480]" data-testid="add-customer-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Customer
          </Button>
        </div>

        <div className="relative mb-4 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            className="pl-9"
            placeholder="Search name, email, address, PIN…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="customer-search"
          />
        </div>

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-20 text-gray-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-20 text-gray-500">
                {customers.length === 0 ? "No customers yet. Add your first one." : "No matches for your search."}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead className="text-right">Acres</TableHead>
                    <TableHead className="w-24" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.slice(0, 500).map((c) => (
                    <TableRow key={c.id} data-testid={`customer-row-${c.id}`}>
                      <TableCell className="font-medium">{c.name}</TableCell>
                      <TableCell className="text-gray-600">{c.company || "—"}</TableCell>
                      <TableCell className="text-gray-600">
                        <div className="text-sm">{c.email || "—"}</div>
                        <div className="text-xs text-gray-400">{c.phone || ""}</div>
                      </TableCell>
                      <TableCell className="text-gray-600 max-w-[220px] truncate">{c.address || "—"}</TableCell>
                      <TableCell className="text-right text-gray-600">{c.acres != null ? c.acres : "—"}</TableCell>
                      <TableCell>
                        <div className="flex gap-1 justify-end">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`edit-customer-${c.id}`}><Edit className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => remove(c)} data-testid={`delete-customer-${c.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? "Edit Customer" : "Add Customer"}</DialogTitle>
              <DialogDescription>Enter details or look up NC parcel data by address.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Full name *" value={form.name} onChange={(e) => setField("name", e.target.value)} data-testid="customer-form-name" />
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Email" value={form.email} onChange={(e) => setField("email", e.target.value)} data-testid="customer-form-email" />
                <Input placeholder="Phone" value={form.phone} onChange={(e) => setField("phone", e.target.value)} data-testid="customer-form-phone" />
              </div>
              <Input placeholder="Company" value={form.company} onChange={(e) => setField("company", e.target.value)} data-testid="customer-form-company" />

              <div className="rounded-md border border-zinc-200 p-3 space-y-2.5">
                <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">Location source — check the one that's authoritative</p>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={form.location_source === "address"} onChange={() => setField("location_source", "address")} data-testid="loc-source-address" className="rounded border-zinc-300 accent-[#00341d]" />
                  <Input placeholder="Property address (for lookup)" value={form.address} onChange={(e) => onAddressChange(e.target.value)} data-testid="customer-form-address" autoComplete="off" data-lpignore="true" data-1p-ignore="true" />
                  <Button type="button" variant="outline" onClick={runLookup} disabled={lookingUp || form.location_source !== "address"} data-testid="customer-lookup-btn" className="shrink-0">
                    {lookingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                    <span className="ml-1 hidden sm:inline">Look up</span>
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={form.location_source === "parcel"} onChange={() => setField("location_source", "parcel")} data-testid="loc-source-parcel" className="rounded border-zinc-300 accent-[#00341d]" />
                  <Input placeholder="PIN / Parcel ID" value={form.pin} onChange={(e) => setField("pin", e.target.value)} data-testid="customer-form-pin" />
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={form.location_source === "coordinates"} onChange={() => setField("location_source", "coordinates")} data-testid="loc-source-coordinates" className="rounded border-zinc-300 accent-[#00341d]" />
                  <Input placeholder="Coordinates (lat, lng)" value={form.coordinates} onChange={(e) => setField("coordinates", e.target.value)} data-testid="customer-form-coordinates" autoComplete="off" data-lpignore="true" data-1p-ignore="true" />
                </div>
                {form.location_source !== "address" ? (
                  <p className="text-xs text-amber-600">Address auto-lookup is off — your {form.location_source === "parcel" ? "Parcel ID" : "Coordinates"} will not be overwritten.</p>
                ) : null}
              </div>

              <Input placeholder="Display address (shown on the quote)" value={form.display_address} onChange={(e) => setField("display_address", e.target.value)} data-testid="customer-form-display-address" autoComplete="off" data-lpignore="true" data-1p-ignore="true" />

              <Input placeholder="Acres" value={form.acres} onChange={(e) => setField("acres", e.target.value)} data-testid="customer-form-acres" />
              <Input placeholder="Notes" value={form.notes} onChange={(e) => setField("notes", e.target.value)} data-testid="customer-form-notes" />
              {propInfo ? (
                <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800 space-y-0.5" data-testid="customer-property-info">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold flex items-center gap-1"><RefreshCw className="w-3 h-3" /> NC Parcel data</div>
                    <button
                      type="button"
                      onClick={clearLookup}
                      className="flex items-center gap-1 text-red-600 hover:text-red-700 font-semibold"
                      data-testid="customer-clear-parcel"
                    >
                      <X className="w-3 h-3" /> Clear
                    </button>
                  </div>
                  {propInfo.owner_name && <div>Owner: {propInfo.owner_name}</div>}
                  {propInfo.county && <div>County: {propInfo.county}</div>}
                  {propInfo.acreage != null && <div>Acreage: {Math.round(propInfo.acreage * 100) / 100}</div>}
                  {propInfo.total_value != null && <div>Tax value: ${Number(propInfo.total_value).toLocaleString()}</div>}
                  {preLookup ? <div className="text-emerald-600 italic pt-1">Clear restores your previous PIN &amp; acres.</div> : null}
                </div>
              ) : null}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-[#00341d] hover:bg-[#00251480]" data-testid="customer-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editing ? "Save changes" : "Add customer"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default CustomersPage;
